import React, { useEffect, useState } from 'react';
import { array } from 'prop-types';

import { connect } from 'react-redux';

import { capitalize, isEmpty } from 'lodash';

import MaterialTable from 'material-table';

import * as WizardActions from '../../../actions/wizardActions';

import classes from './ProfileTable.module.css';

const CreateSensorTable = ({
    conversions,
    equipmentTypes,
    initializeTargetSensors,
    sensorTypes,
    targetDevices,
    targetSensors,
    units,
    ...props
}) => {

    useEffect(() => {
        initializeTargetSensors();

        return () => {
            targetDevices.forEach(row => {
                row.tableData = {};
            });
        };
    }, []);

    const nameSorter = (a, b) => {
        return sorter(a, b, 'name');
    };

    const sorter = (a, b, property) => {
        const valueA = a[property].toUpperCase();
        const valueB = b[property].toUpperCase();

        if (valueA < valueB) {
            return -1;
        }
        if (valueA > valueB) {
            return 1;
        }

        return 0;
    };

    const getEquipmentTypeName = equipmentTypeId => {
        return equipmentTypes.find(type => type.id === equipmentTypeId).name;
    };

    const getSensorTypeName = sensorTypeId => {
        return sensorTypes[0].items.find(type => type.id === sensorTypeId).name;
    };

    const getDisplayUnitName = displayUnitId => {
        let targetUnit;

        units.forEach(unitGroup => {
            unitGroup.items.forEach(item => {
                if (item.id === displayUnitId) {
                    targetUnit = {...item};
                }
            });
        });

        if (targetUnit) {
            return targetUnit.name;
        }

        return '';
    };

    const getConversionName = conversionId => {
        if (isEmpty(conversionId)) return '';

        return conversions.find(type => type.id === conversionId).name;
    };

    const identifierCustomFilterAndSort = (term, rowData) => {
        if (rowData.oBIXPath) {
            return rowData.oBIXPath.toLowerCase().includes(term.toLowerCase());
        }
        else if (rowData.deviceId) {
            return rowData.deviceId.toString().toLowerCase().includes(term.toLowerCase());
        }

        return;
    };
    
    // Define columns prior to injecting them into component's state
    const columns = [
        {
            title:      'Name',
            field:      'name',
            render:     rowData => <span className={rowData.isExcluded ? classes.excluded : ''}>{rowData.name}</span>,
            customSort: nameSorter
        },
        {
            title:                 'Identifier',
            customFilterAndSearch: identifierCustomFilterAndSort,
            render:                rowData => {
                if (rowData.oBIXPath) return <span className={rowData.isExcluded ? classes.excluded : ''}>{rowData.oBIXPath}</span>;
                if (rowData.deviceId) return <span className={rowData.isExcluded ? classes.excluded : ''}>{rowData.deviceId}</span>;

                return '';
            }
        },
        {
            title:                 'Profile',
            field:                 'profileName',
            customFilterAndSearch: (term, rowData) => rowData.profileName.toLowerCase().includes(term.toLowerCase()),
            render:                rowData => <span className={rowData.isExcluded ? classes.excluded : ''}>{rowData.profileName}</span>
        },
        {
            title:  'Equipment Type',
            field:  'equipmentType',
            render: rowData => <span className={rowData.isExcluded ? classes.excluded : ''}>{getEquipmentTypeName(rowData.equipmentType)}</span>
        }
    ];

    // This is a WORK AROUND for a Material Table (as of v1.68) issue where column sort state was lost when parent
    // component's state updates. This technique is not meant for global adoption; check on the current
    // state of Material Table & determine if a solution has been introduced before copy/pasting this code.
    // That said, lifting the columns into the parent's state preserves it and prevents state change from
    // "dropping" the current column sort order.
    //
    // https://github.com/mbrn/material-table/issues/1265
    const [tableColumns] = useState(columns);

    if (!targetDevices.length) return null;

    return (
        <div className={classes.ProfileTable}>
            <div className={classes.title}>
                <h2>Create Sensors</h2>
            </div>
            <div className={classes.tableContainer}>
                <MaterialTable
                    options={{
                        paging:    false,
                        showTitle: false,
                        search:    false,
                        selection: false,
                        toolbar:   false,
                        filtering: true
                    }}
                    columns={tableColumns}
                    data={targetDevices}
                    detailPanel={() => (
                        <div className={classes.subTable}>
                            <MaterialTable
                                options={{
                                    paging:    false,
                                    showTitle: false,
                                    search:    false,
                                    selection: false,
                                    toolbar:   false
                                }}
                                columns={[
                                    { title: 'Sensor Name', field: 'name' },
                                    {
                                        title:  'Sensor Type',
                                        render: rowData => getSensorTypeName(rowData.sensorTypeId)
                                    },
                                    {
                                        title:  'Object Type',
                                        render: rowData => rowData.profileObjectType
                                    },
                                    {
                                        title:  'Display Data Type',
                                        render: rowData => capitalize(rowData.displayDataType)
                                    },
                                    {
                                        title:  'Display Unit',
                                        render: rowData => getDisplayUnitName(rowData.presentationUnitId)
                                    },
                                    {
                                        title:  'Conversion',
                                        render: rowData => getConversionName(rowData.conversionId)
                                    }
                                ]}
                                data={targetSensors}
                            />
                        </div>
                    )}
                />
            </div>
        </div>
    );
};

CreateSensorTable.defaultProps = {
    conversions:    [],
    equipmentTypes: [],
    sensorTypes:    [],
    targetDevices:  [],
    targetSensors:  [],
    units:          []
};

CreateSensorTable.propTypes = {
    conversions:    array,
    equipmentTypes: array,
    sensorTypes:    array,
    targetDevices:  array,
    targetSensors:  array,
    units:          array
};

const mapStateToProps = state => {
    return {
        conversions:    state.architect.conversions,
        equipmentTypes: state.equipmentTypes.data,
        sensorTypes:    state.sensorTypes.data,
        targetDevices:  state.wizard.targetDevices,
        targetSensors:  state.wizard.targetSensors,
        units:          state.architect.rawUnits
    };
};

const mapDispatchToProps = {
    ...WizardActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateSensorTable);
